import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn } from "../variants";
import { FaGithub, FaExternalLinkAlt, FaTimes, FaPlay } from "react-icons/fa";
import Modal from "react-modal";
import Huguette from "../assets/huguette.mp4";
import Img1 from "../assets/portfolio-img1.png";
import Img2 from "../assets/portfolio-img2.png";
import Img3 from "../assets/portfolio-img3.png";

const projects = [
  {
    id: 1,
    title: "Huguette",
    description: "Application mobile de VTC pour femme",
    image: Img1,
    tags: ["React Native", "Expo", "MongoDB"],
    color: "bg-gradient-to-br from-gold to-gold-rose",
    hasVideo: true,
    link: null,
  },
  {
    id: 2,
    title: "My Movies",
    description: "Site qui répertorie les films",
    image: Img2,
    tags: ["React", "Tailwind", "API"],
    color: "bg-gradient-to-br from-navy to-navy-dark",
    hasVideo: false,
    link: "https://mesfilms-frontend.vercel.app/",
  },
  {
    id: 3,
    title: "React Journey",
    description: "Site sur les concepts de React",
    image: Img3,
    tags: ["React", "Tailwind"],
    color: "bg-gradient-to-br from-sage to-sage-dark",
    hasVideo: false,
    link: "https://reactjourney-2.vercel.app/?filter=",
  },
];

const Work = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <section className="section bg-pearl" id="work">
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          variants={fadeIn("up", 0.3)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.3 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-5 py-2 bg-white border border-stone/40 font-mono font-medium text-sm tracking-wider mb-4 shadow-elegant">
            RÉALISATIONS
          </span>
          <h2 className="h2">
            Mes Derniers
            <br />
            <span className="relative inline-block">
              Projets
              <span className="absolute -bottom-2 left-0 w-full h-3 bg-gradient-to-r from-gold/40 to-terracotta/40 -z-10 blur-sm" />
            </span>
          </h2>
          <p className="text-lg text-charcoal/70 mt-6 max-w-2xl mx-auto">
            Une sélection de mes projets récents développés avec passion et
            expertise
          </p>

          {/* GitHub Link */}
          <motion.div
            variants={fadeIn("up", 0.4)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: false, amount: 0.3 }}
            className="mt-8"
          >
            <a
              href="https://github.com/tizen21"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-white border border-stone/40 shadow-elegant hover:shadow-elegant-hover hover:-translate-y-1 transition-all font-primary font-medium"
            >
              <FaGithub className="text-2xl" />
              Voir plus sur GitHub
            </a>
          </motion.div>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={fadeIn("up", 0.3 + index * 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.3 }}
              className={`relative group ${
                index === 0 ? "lg:col-span-2" : ""
              }`}
            >
              <div className="relative overflow-hidden border border-stone/30 bg-white shadow-elegant group-hover:shadow-elegant-xl group-hover:-translate-y-2 transition-all duration-300">
                {/* Image */}
                <div className="relative h-64 lg:h-80 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Action button */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    {project.hasVideo ? (
                      <button
                        onClick={() => setModalIsOpen(true)}
                        className="px-6 py-3 bg-gradient-to-r from-gold to-gold-rose text-white font-primary font-medium border border-white/30 shadow-gold-glow hover:shadow-elegant-lg transition-all"
                      >
                        ▶ Voir la démo
                      </button>
                    ) : (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white font-primary font-medium border border-navy-light shadow-elegant hover:shadow-elegant-lg transition-all"
                      >
                        Visiter
                        <FaExternalLinkAlt />
                      </a>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 lg:p-8">
                  <h3 className="h3 mb-2">{project.title}</h3>
                  <p className="text-charcoal/70 mb-4">
                    {project.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-sand-light border border-sand-dark/30 font-mono text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Color accent */}
                <div
                  className={`absolute -top-3 -right-3 w-20 h-20 ${project.color} border border-white/30 rounded-full blur-lg opacity-50`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Video Modal - Enhanced Cinematic Design */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className="flex items-center justify-center h-full outline-none"
        overlayClassName="fixed inset-0 bg-charcoal/95 backdrop-blur-md z-50 flex items-center justify-center p-4"
        ariaHideApp={false}
        closeTimeoutMS={300}
      >
        <AnimatePresence>
          {modalIsOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="relative max-w-6xl w-full"
            >
              {/* Decorative background elements */}
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-terracotta/10 to-gold-rose/20 blur-3xl opacity-50 animate-pulse" />

              {/* Main container */}
              <div className="relative bg-ivory border-2 border-stone/40 shadow-2xl overflow-hidden">
                {/* Top bar with project info */}
                <div className="relative bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal border-b-2 border-gold/30 px-8 py-5">
                  {/* Decorative accent */}
                  <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-gold/20 to-transparent" />

                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-gold to-gold-rose border border-white/30 shadow-gold-glow">
                        <FaPlay className="text-white text-sm ml-0.5" />
                      </div>
                      <div>
                        <h3 className="font-primary font-bold text-2xl text-ivory tracking-tight">
                          Huguette
                        </h3>
                        <p className="font-mono text-xs text-ivory/60 tracking-wider mt-0.5">
                          DÉMO DE L'APPLICATION
                        </p>
                      </div>
                    </div>

                    {/* Close button */}
                    <button
                      onClick={() => setModalIsOpen(false)}
                      className="group relative flex items-center justify-center w-12 h-12 bg-white/10 border border-white/20 hover:bg-gradient-to-br hover:from-gold hover:to-gold-rose hover:border-white/40 transition-all duration-300 hover:scale-110"
                      aria-label="Fermer la modal"
                    >
                      <FaTimes className="text-ivory text-lg group-hover:rotate-90 transition-transform duration-300" />
                    </button>
                  </div>
                </div>

                {/* Video container */}
                <div className="relative bg-charcoal p-6 lg:p-8">
                  {/* Video wrapper with cinematic frame */}
                  <div className="relative aspect-video bg-black border-2 border-stone/50 shadow-inner overflow-hidden">
                    {/* Decorative corners */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-gold" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-gold" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-gold" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-gold" />

                    <video
                      autoPlay
                      controls
                      className="w-full h-full object-contain"
                      playsInline
                    >
                      <source src={Huguette} type="video/mp4" />
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  </div>

                  {/* Project metadata */}
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <span className="inline-block px-4 py-2 bg-gradient-to-br from-gold to-gold-rose border border-white/30 font-mono text-xs font-medium text-white shadow-gold-glow">
                      React Native
                    </span>
                    <span className="inline-block px-4 py-2 bg-navy border border-navy-light font-mono text-xs font-medium text-white shadow-elegant">
                      Expo
                    </span>
                    <span className="inline-block px-4 py-2 bg-sage border border-sage-dark font-mono text-xs font-medium text-white shadow-elegant">
                      MongoDB
                    </span>
                    <span className="ml-auto font-mono text-xs text-ivory/50 tracking-wider">
                      Application mobile de VTC pour femmes
                    </span>
                  </div>
                </div>

                {/* Bottom decorative border */}
                <div className="h-2 bg-gradient-to-r from-gold via-terracotta to-gold-rose" />
              </div>

              {/* Grain texture overlay */}
              <div className="absolute inset-0 pointer-events-none grain opacity-30" />
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>
    </section>
  );
};

export default Work;
